import PhotoSwipe from 'photoswipe';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import PhotoSwipeVideoPlugin from 'photoswipe-video-plugin';
import { SWIPER } from '../../utils/constants';

let lightbox = null;
let isLightboxInitialized = false;
let pausedVideoEls = [];

// A poster image is required for the video plugin to ever mark a slide as
// loaded (see photoswipe-video-plugin's preloadVideoPoster) — fall back to a
// blank pixel so slides without a Shopify-generated preview image still load.
const BLANK_POSTER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBTAA7';

const buildSlideData = (slideEl) => {
  const { lightboxImage, lightboxVideoSrc, lightboxWidth, lightboxHeight, lightboxPoster } = slideEl.dataset;
  const width = Number(lightboxWidth);
  const height = Number(lightboxHeight);
  if (!width || !height) return null;

  if (lightboxVideoSrc) {
    return { type: 'video', videoSrc: lightboxVideoSrc, width, height, msrc: lightboxPoster || BLANK_POSTER };
  }

  if (lightboxImage) {
    const slideImageEl = slideEl.querySelector('img');
    return { src: lightboxImage, width, height, msrc: slideImageEl?.currentSrc };
  }

  return null;
};

const resumeVideo = async (videoEl) => {
  try {
    await videoEl.play();
  } catch {
    // Playback can be refused (e.g. iOS Low Power Mode) — leave the video paused
  }
};

const getLightbox = () => {
  if (lightbox) return lightbox;

  lightbox = new PhotoSwipeLightbox({
    pswpModule: PhotoSwipe,
    // Close only via the close button — not by clicking the backdrop or a
    // non-zoomable slide (e.g. video, or an image already at its zoom limit).
    bgClickAction: false,
    clickToCloseNonZoomable: false,
    // PhotoSwipe's default "fit" zoom never upscales past a slide's native
    // resolution, so a lower-resolution image displays smaller than its
    // neighbours instead of matching them — always fit the viewport instead.
    initialZoomLevel: (zoomLevel) => {
      const { panAreaSize, elementSize } = zoomLevel;
      return Math.min(panAreaSize.x / elementSize.x, panAreaSize.y / elementSize.y);
    }
  });

  // the plugin registers itself onto the lightbox via events — no reference needed
  new PhotoSwipeVideoPlugin(lightbox, {});
  // Registered once — listeners on the singleton persist across opens, so a
  // per-open registration would accumulate and replay on every close. Resumes
  // only the videos the matching open actually paused.
  lightbox.on('destroy', () => {
    pausedVideoEls.forEach(resumeVideo);
    pausedVideoEls = [];
  });
  lightbox.init();
  return lightbox;
};

const openGalleryLightbox = (galleryEl, clickedSlideEl) => {
  const slideEls = [...galleryEl.querySelectorAll(SWIPER.SLIDE_CLASS)];
  const slides = slideEls
    .map((slideEl) => ({ slideEl, data: buildSlideData(slideEl) }))
    .filter((slide) => slide.data);
  if (!slides.length) return;

  const activeIndex = slides.findIndex((slide) => slide.slideEl === clickedSlideEl);

  // Pause playing inline gallery videos so they don't run under the lightbox;
  // the singleton's destroy handler resumes exactly these.
  pausedVideoEls = [...galleryEl.querySelectorAll('video')].filter((videoEl) => !videoEl.paused);
  pausedVideoEls.forEach((videoEl) => videoEl.pause());

  const lightboxInstance = getLightbox();
  lightboxInstance.loadAndOpen(Math.max(activeIndex, 0), slides.map((slide) => slide.data));
};

const initProductGalleryLightbox = () => {
  if (isLightboxInitialized) return;

  document.addEventListener('click', (event) => {
    const galleryEl = event.target.closest('[data-product-gallery]');
    if (!galleryEl) return;

    // Keyboard path: the sr-only open button opens at the active slide.
    if (event.target.closest('[data-gallery-lightbox-open]')) {
      openGalleryLightbox(galleryEl, galleryEl.querySelector(SWIPER.SLIDE_ACTIVE_CLASS));
      return;
    }

    // Inline gallery videos keep their native controls — clicking them
    // plays/pauses rather than opening the lightbox.
    if (event.target.closest('button, label, input, video')) return;
    const clickedSlideEl = event.target.closest(SWIPER.SLIDE_CLASS);
    openGalleryLightbox(galleryEl, clickedSlideEl);
  });

  isLightboxInitialized = true;
};

export default initProductGalleryLightbox;
